import { reaction } from 'mobx';
import {
  ringCashOutStates,
  ringMaxStates,
  RINGS,
  textSlotsAlphaEnablingScaleValues,
} from '../powerOfGodsCore/constants/matrixConstants';
import {
  AnimationConfigs,
  PowerOfGodsSoundsNames,
} from '../powerOfGodsCore/constants/globalConstants';
import { MatrixStore } from '../powerOfGodsStores/matrixStore';
import { MatrixView } from '../powerOfGodsViewers/matrixView';
import { userInfoStore } from '../../../store/userInfoStore';
import { soundService } from '../../../services/baseSoundService/soundService';

export class MatrixController {
  constructor(parentContainer, initialStates) {
    this.store = new MatrixStore();
    this.reversingSoundCounter = 0;
    RINGS.forEach((ring, index) => {
      this.store.setRingState(ring, initialStates[index]);
    });
    this.view = new MatrixView(parentContainer, this.store.ringState);
    const actions = {
      big: {
        loader: (name, idx, isReverse) => {
          this.view.animateControlStripScaleToIndex(name, idx, {
            onStart: () => {
              this.switchSoundOnLoad(isReverse);
              this.onLoadScaleStartFromView(name);
            },
            onUpdate: (ring, index, scale) => this.onLoadScaleUpdateFromView(ring, index, scale),
            onComplete: (ring) => this.onRingScaleCompleteFromView(ring),
            duration: AnimationConfigs.lineLoadingDuration,
          });
        },
        cashOut: (name, idx) => {
          this.view.animateControlStripScaleToIndex(name, idx, {
            onStart: () => this.onLoadScaleStartFromView(name),
            onUpdate: (ring, index, scale) => this.onLoadScaleUpdateFromView(ring, index, scale),
            onComplete: (ring) => this.onBigRingCashOutCompleteFromView(ring),
            duration: AnimationConfigs.resetBigRingForBonusCompleteDuration,
          });
        },
      },
      middle: {
        loader: (name, idx, isReverse) => {
          this.view.animateControlStripScaleToIndex(name, idx, {
            onStart: () => {
              this.switchSoundOnLoad(isReverse);
              this.onLoadScaleStartFromView(name);
            },
            onUpdate: (ring, index, scale) => this.onLoadScaleUpdateFromView(ring, index, scale),
            onComplete: (ring) => this.onRingScaleCompleteFromView(ring),
            duration: AnimationConfigs.lineLoadingDuration,
          });
        },
        cashOut: (name, idx) => {
          this.view.animateControlStripScaleForLineCashOut(name, idx, {
            onStart: () => this.onCashOutScaleStartFromView(name),
            onUpdate: (ring, scale) => this.onCashOutScaleUpdateFromView(ring, scale),
            onComplete: (ring) => this.onCashOutScaleCompleteFromView(ring),
            duration: AnimationConfigs.lineLoadingDuration,
          });
        },
      },
      small: {
        loader: (name, idx, isReverse) => {
          this.view.animateControlStripScaleToIndex(name, idx, {
            onStart: () => {
              this.switchSoundOnLoad(isReverse);
              this.onLoadScaleStartFromView(name);
            },
            onUpdate: (ring, index, scale) => this.onLoadScaleUpdateFromView(ring, index, scale),
            onComplete: (ring) => this.onRingScaleCompleteFromView(ring),
            duration: AnimationConfigs.lineLoadingDuration,
          });
        },
        cashOut: (name, idx) => {
          this.view.animateControlStripScaleForLineCashOut(name, idx, {
            onStart: () => this.onCashOutScaleStartFromView(name),
            onUpdate: (ring, index, scale) => this.onCashOutScaleUpdateFromView(ring, index, scale),
            onComplete: (ring) => this.onCashOutScaleCompleteFromView(ring),
            duration: AnimationConfigs.lineCashOutDuration,
          });
        },
      },
    };

    RINGS.forEach((name) => {
      reaction(
        () => this.store.ringState[name],
        (newIdx, oldIdx = newIdx) => {
          const isReverse = newIdx < oldIdx;
          this.store.setRingActionTypeReverse(name, isReverse);
          this.store.setRingScaleComplete(name, false);

          const mode = this.getMode(name, newIdx, oldIdx);

          if (isReverse && oldIdx === ringCashOutStates[name]) {
            this.view.animateLastTextInLoader(name, false);
          }
          actions[name][mode](name, newIdx, isReverse);
        }
      );
    });
    RINGS.forEach((name) => {
      reaction(
        () => this.store.ringLoadingComplete[name],
        (complete) => {
          this.handleLoadingCompleteStateChange(name, complete);
        }
      );
    });
  }

  handleLoadingCompleteStateChange(name, complete) {
    const currIndex = this.store.ringState[name];
    const isReverse = this.store.ringActionTypes[name].reverse;
    if (name === 'big' && complete && currIndex === ringMaxStates.big) {
      this.store.setRingScaleComplete(name, true);
      return;
    }
    const isNextIndexIsCashOut = currIndex === ringCashOutStates[name];
    const isCashOutActionIndex = currIndex === ringMaxStates[name];
    if (complete) {
      this.handleLoadingComplete(
        name,
        isReverse,
        isNextIndexIsCashOut,
        isCashOutActionIndex,
        currIndex
      );
    } else {
      this.handleLoadingStart(name, isReverse, currIndex);
    }
  }

  switchSoundOnLoad(isReverse) {
    if (!isReverse) {
      soundService.play(PowerOfGodsSoundsNames.LoadRing);
    } else if (isReverse && this.store.isMedusaReversing) {
      // eslint-disable-next-line
      this.reversingSoundCounter++;
      soundService.play(PowerOfGodsSoundsNames.ReverseLoadRing1Step);
    }
  }

  doShake() {
    this.view.playShakeAnimation();
  }

  handleLoadingStart(name, isReverse, currIndex) {
    const isCashOut = currIndex !== ringCashOutStates[name];
    const { isMedusaReversing } = this.store;

    if (!isReverse && currIndex > 1) {
      const borderIndex = currIndex - 2;
      const isBorderEnabled = this.view.borderSlots[name][borderIndex].isEnabled;
      if (!isBorderEnabled) {
        this.view.animateBorderSlotColor(name, borderIndex, true);
      }
    }
    if (isReverse && isCashOut && isMedusaReversing) {
      this.view.animateReverseLoadingImages(name, currIndex, true);
    }
    if (isReverse && isCashOut && name === RINGS[0]) {
      userInfoStore.getBalance();
    }
    if (!isReverse) {
      this.view.animateLightLoaders(name, true);
      this.view.animateFXBar(name);
    }
  }

  handleLoadingComplete(name, isReverse, isNextIndexIsCashOut, isCashOutIndex, currIndex) {
    if (isNextIndexIsCashOut) {
      this.store.setRingActionTypeLineCashOut(name, !isNextIndexIsCashOut);
      this.view.animateLastTextInLoader(name, true);
    }
    if (isCashOutIndex) {
      this.store.setRingActionTypeLineCashOut(name, isCashOutIndex);
      this.store.setRingState(name, ringCashOutStates[name]);
    }
    if (isReverse) {
      if (this.store.isMedusaReversing) {
        this.reversingSoundCounter = 0;
        this.view.animateReverseLoadingImages(name, currIndex, false);
      }
      const maxState = ringMaxStates[name];
      const hasPrevBorder = currIndex > 0 && currIndex !== maxState;
      if (hasPrevBorder) {
        const slot = this.view.borderSlots[name][currIndex - 1];
        if (slot.isEnabled) {
          this.view.animateBorderSlotColor(name, currIndex - 1, false);
        }
      }
    }
    if (!isReverse) {
      this.view.animateLightLoaders(name, false);
    }
  }

  onLoadScaleStartFromView = () => {
    // this.store.setRingScaleComplete(name, false);
  };

  onRingScaleCompleteFromView(name) {
    this.store.setRingScaleComplete(name, true);
  }

  onBigRingCashOutCompleteFromView(name) {
    this.store.setRingScaleComplete(name, true);
    this.store.setIsInBonusScene(false);
  }

  onLoadScaleUpdateFromView(name, index, currentScale) {
    const { cashOut: isCashOut } = this.store.ringActionTypes[name];
    const thresholds = textSlotsAlphaEnablingScaleValues[name];
    const texts = this.view.textSlots[name] || [];
    if (!texts.length) return;

    texts.forEach((entry, i) => {
      const threshold = thresholds[i];
      const enabled = entry.isEnabled;

      if (!enabled && currentScale >= threshold) {
        this.view.animateTextSlotColor(name, i, true);
        entry.isEnabled = true;
      } else if (enabled && currentScale <= threshold) {
        this.view.animateTextSlotColor(name, i, false);
        entry.isEnabled = false;
      }
    });

    if (isCashOut) {
      for (let i = texts.length - 1; i >= 0; i -= 1) {
        const entry = texts[i];
        if (entry.isEnabled && currentScale <= thresholds[i]) {
          this.view.animateTextSlotColor(name, i, false);
          entry.isEnabled = false;
          break;
        }
      }
    }
  }

  onCashOutScaleStartFromView = () => {
    // this.store.setRingScaleComplete(name, false);
  };

  onCashOutScaleCompleteFromView(name) {
    this.store.setRingScaleComplete(name, true);
    this.store.setRingState(name, ringMaxStates[name] - 1);
  }

  changeBonusModeActiveState(state) {
    this.store.setIsInBonusScene(state);
  }

  onCashOutScaleUpdateFromView(name, currentScale) {
    const currentRingTexts = this.view.textSlots[name] || [];
    const index = textSlotsAlphaEnablingScaleValues[name].length - 1;
    const checkPoint = textSlotsAlphaEnablingScaleValues[name][index];
    const isTextEnabled = currentRingTexts[index].isEnabled;
    if (isTextEnabled && currentScale <= checkPoint) {
      this.view.animateLastTextInLoader(name, true);
    }
  }

  resetAfterBonus() {
    this.store.setRingActionTypeCashOut(RINGS[0], false);
    this.store.setRingState(RINGS[0], 0);
  }

  doMatrixCashOut({ big, middle, small }) {
    this.store.resetStatesForCashOut({ big, middle, small });
  }

  skipWinAccelerationAnimations() {
    this.view.skipWinAccelerationAnimations();
  }

  getMode(name, newIdx, oldIdx) {
    const isReverse = newIdx < oldIdx;

    if (name === RINGS[0]) {
      const { isInBonusScene } = this.store;
      if (isInBonusScene && !newIdx) {
        return 'cashOut';
      }
    }

    const isNextIndexIsCashOut = newIdx === ringCashOutStates[name];
    const isCashOutAction = isNextIndexIsCashOut && isReverse;
    return isCashOutAction ? 'cashOut' : 'loader';
  }

  doNoWinAnimation() {
    // this.view.animateRingsForNoWin();
  }

  handleSpinResult(results, symbol, winInfo, bonusInfo) {
    this.store.setResponseReady(true);
    this.store.setResponseData(results, symbol, winInfo, bonusInfo);
  }
}
